import { isNil } from 'lodash'
import {
  GeckoComponentDataFrame,
  GeckoComponentModel,
  GeckoComponentModelType,
  GeckoMeasurement,
  GeckoMeasurementUnit,
} from 'types'

const CONSISTENT_BIN_AREA_COMPONENTS = new Set([
  //these are the component models where we can just calculate the area from the binning
  // since the bin areas don't change
  GeckoComponentModelType.VerticalTankShell,
  GeckoComponentModelType.HorizontalTankShell,
  GeckoComponentModelType.PipeSection,
  GeckoComponentModelType.TankFloor,
  GeckoComponentModelType.TankFlatRoof,
  GeckoComponentModelType.VerticalTankFoundation,
])

const getSquareMeterComponentAreaNumber = (
  componentModel?: GeckoComponentModel
): number => {
  if (!componentModel) {
    //If we don't have a component model, we can't calculate the area
    return GeckoMeasurement.CreateFromValue(NaN, GeckoMeasurementUnit.Meters)
      .num
  }
  switch (componentModel.type) {
    case GeckoComponentModelType.VerticalTankShell: {
      // pi * d * h
      return componentModel.height
        .convertTo(GeckoMeasurementUnit.Meters)
        .scale(
          componentModel.radius
            .convertTo(GeckoMeasurementUnit.Meters)
            .scale(2)
            .scale(Math.PI).num
        ).num
    }
    case GeckoComponentModelType.HorizontalTankShell: {
      // pi * d * h
      return componentModel.length
        .convertTo(GeckoMeasurementUnit.Meters)
        .scale(
          componentModel.radius
            .scale(2)
            .scale(Math.PI)
            .convertTo(GeckoMeasurementUnit.Meters).num
        ).num
    }
    case GeckoComponentModelType.PipeSection: {
      // pi * d * l
      return componentModel.length
        .convertTo(GeckoMeasurementUnit.Meters)
        .scale(
          componentModel.radius
            .scale(2)
            .scale(Math.PI)
            .convertTo(GeckoMeasurementUnit.Meters).num
        ).num
    }
    case GeckoComponentModelType.TankFloor: {
      // pi * r^2
      return (
        componentModel.radius
          .convertTo(GeckoMeasurementUnit.Meters)
          //feels unsafe but doubt it tbh
          .scale(
            componentModel.radius.convertTo(GeckoMeasurementUnit.Meters).num
          )
          .scale(Math.PI).num
      )
    }
    case GeckoComponentModelType.TankFlatRoof: {
      // pi * r^2
      return (
        componentModel.radius
          .convertTo(GeckoMeasurementUnit.Meters)
          //feels unsafe but doubt it tbh
          .scale(
            componentModel.radius.convertTo(GeckoMeasurementUnit.Meters).num
          )
          .scale(Math.PI).num
      )
    }
    case GeckoComponentModelType.VerticalTankFoundation: {
      // pi * d * l
      return componentModel.height
        .convertTo(GeckoMeasurementUnit.Meters)
        .scale(
          componentModel.radius
            .convertTo(GeckoMeasurementUnit.Meters)
            .scale(2)
            .scale(Math.PI).num
        ).num
    }
    case GeckoComponentModelType.LegacyCone: {
      const coneInnerRadius = componentModel.componentBounds.start.y.convertTo(
        GeckoMeasurementUnit.Meters
      )
      const coneOuterRadius = componentModel.componentBounds.end.y.convertTo(
        GeckoMeasurementUnit.Meters
      )
      const coneInnerCircle = coneInnerRadius
        .scale(coneInnerRadius.num)
        .scale(Math.PI)
      const coneOuterCircle = coneOuterRadius
        .scale(coneOuterRadius.num)
        .scale(Math.PI)
      return coneOuterCircle.minus(coneInnerCircle).num
    }
    default: {
      //TODO add the area to other component models
      return GeckoMeasurement.CreateFromValue(NaN, GeckoMeasurementUnit.Meters)
        .num
    }
  }
}
export const calculateComponentArea = (
  componentModel?: GeckoComponentModel
) => {
  //TODO add these to methods to the component model  (will refactor with swe support later)
  const metersArea = getSquareMeterComponentAreaNumber(componentModel)
  return GeckoMeasurement.CreateFromValue(
    metersArea,
    GeckoMeasurementUnit.SquareMeters
  )
}

export const calculateActualSquareFootageArea = ({
  binning,
  componentModel,
  raw2dArray,
}: {
  binning?: GeckoComponentDataFrame['data']['binning']
  componentModel?: GeckoComponentModel
  raw2dArray?: readonly (readonly number[])[]
}) => {
  if (!raw2dArray || !binning || !componentModel) {
    //If we don't have a binning or raw array, we can't calculate the area
    return GeckoMeasurement.CreateFromValue(
      NaN,
      GeckoMeasurementUnit.SquareMeters
    )
  }
  if (componentModel.type === GeckoComponentModelType.LegacyCone) {
    //cry here
    const binHeight = binning.bin_size.height.convertTo(
      GeckoMeasurementUnit.Meters
    )
    const componentStartRadius =
      componentModel.componentBounds.start.y.convertTo(
        GeckoMeasurementUnit.Meters
      )

    const raw2dArrayArea = raw2dArray.reduce((acc, val, index) => {
      //bascially we iterate radially outwards and find the percentage of that ring that is filled, do the ring math, multiple by the percent and
      //add them all together
      //this is all done in meters
      const totalNumbeOfBins = val.length
      const filledBinCount = val.filter((x) => !isNil(x) && !isNaN(x)).length
      const filledBinPercentage = filledBinCount / totalNumbeOfBins
      const innerCircleArea =
        (index * binHeight.num + componentStartRadius.num) ** 2 * Math.PI
      const outerCircleArea =
        ((index + 1) * binHeight.num + componentStartRadius.num) ** 2 * Math.PI
      const ringArea = outerCircleArea - innerCircleArea
      const filledRingArea = ringArea * filledBinPercentage
      return acc + filledRingArea
    }, 0)
    return GeckoMeasurement.CreateFromValue(
      raw2dArrayArea,
      GeckoMeasurementUnit.SquareMeters
    )
  }
  if (CONSISTENT_BIN_AREA_COMPONENTS.has(componentModel.type)) {
    //If we have a consistent bin area, we can just multiply the bin area by the number of bins
    const validBinCount = raw2dArray
      .flat()
      .filter((x) => !isNil(x) && !isNaN(x)).length
    const binArea = GeckoMeasurement.CreateFromValue(
      binning.bin_size.height
        .convertTo(GeckoMeasurementUnit.Meters)
        .scale(
          binning.bin_size.width.convertTo(GeckoMeasurementUnit.Meters).num
        ).num,
      GeckoMeasurementUnit.SquareMeters
    )
    return binArea.scale(validBinCount)
  }
  return GeckoMeasurement.CreateFromValue(
    NaN,
    GeckoMeasurementUnit.SquareMeters
  )
}
